import type { ReactNode } from 'react';
import styles from './Stepper.module.css';

export interface StepperStep {
  /** Stable id (for React keys). */
  id: string;
  /** Step label rendered below/beside the marker. */
  label: ReactNode;
  /** Optional short description rendered below the label. */
  description?: ReactNode;
}

export interface StepperProps {
  /** Ordered list of steps. */
  steps: StepperStep[];
  /** Zero-based index of the active step. Steps before are 'done'; after are 'pending'. */
  current: number;
  /** Stepper orientation. Default 'horizontal'. */
  orientation?: 'horizontal' | 'vertical';
  /** Accessible label for the list. Default: "Progress". */
  ariaLabel?: string;
}

type StepStatus = 'done' | 'active' | 'pending';

/**
 * Linear stepper for multi-step flows (e.g. MFA setup wizard: Scan → Verify → Save).
 *
 * Renders an ordered list with numbered markers; steps before `current` show a
 * filled check, the current shows the brand number, future steps show a
 * muted number. Each item is announced with its status to screen readers
 * via `aria-current` and the visually-hidden status text.
 */
export function Stepper({
  steps,
  current,
  orientation = 'horizontal',
  ariaLabel = 'Progress',
}: StepperProps) {
  return (
    <ol
      className={[
        styles.list,
        orientation === 'vertical' ? styles.listVertical : styles.listHorizontal,
      ].join(' ')}
      aria-label={ariaLabel}
    >
      {steps.map((step, index) => {
        const status: StepStatus =
          index < current ? 'done' : index === current ? 'active' : 'pending';
        const stepNumber = index + 1;
        return (
          <li
            key={step.id}
            className={[styles.item, styles[`item_${status}`]].join(' ')}
            aria-current={status === 'active' ? 'step' : undefined}
          >
            <span className={styles.marker} aria-hidden="true">
              {status === 'done' ? <CheckGlyph /> : stepNumber}
            </span>
            <span className={styles.body}>
              <span className={styles.label}>
                {step.label}
                <span className={styles.srOnly}>
                  {' '}
                  ({status === 'done'
                    ? 'completed'
                    : status === 'active'
                      ? 'current step'
                      : 'not yet started'})
                </span>
              </span>
              {step.description && (
                <span className={styles.description}>{step.description}</span>
              )}
            </span>
            {index < steps.length - 1 && (
              <span className={styles.connector} aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function CheckGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M3 7 L6 10 L11 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
